import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import SponsorBadge from '../components/SponsorBadge';
import { PostActivityPanel } from '../components/PostActivityPanel';
import { HostTools } from '../components/HostTools';
import { MoreFromHost } from '../components/MoreFromHost';
import { JustCreatedShareNudge } from '../components/JustCreatedShareNudge';
import { ActivityChat } from '../components/ActivityChat';
import { trackEvent } from '../utils/trackEvent';
import { classifyLocation } from '../utils/locationClassify';
import {
  ArrowLeft, MapPin, Calendar, Clock, User, ChevronRight,
  Check, Star, Hand, Package, Car, Navigation, Trash2, Edit2,
  FileText, AlertCircle, Share2, Copy, QrCode, Image, Download,
  Users, Wrench, Box, Radio, Link2 as LinkIcon, Globe, Lock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SIGNAL_ICONS = {
  in: Check,
  interested: Star,
  can_help: Hand,
  bringing_equipment: Package,
  need_equipment: Package,
  can_drive: Car,
  need_ride: Navigation,
  need_volunteers: Users,
  need_tools: Wrench,
  need_supplies: Box,
};

const SIGNAL_COLORS = {
  in: 'var(--lk-sage)',
  interested: 'var(--lk-secondary-orange)',
  can_help: '#8B5CF6',
  bringing_equipment: 'var(--lk-earth-brown)',
  need_equipment: '#D97706',
  can_drive: '#3B82F6',
  need_ride: '#EC4899',
  need_volunteers: '#059669',
  need_tools: '#7C3AED',
  need_supplies: '#D97706',
};

const STATUS_COLORS = {
  idea: { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' },
  confirmed: { bg: '#D1FAE5', text: '#065F46', border: '#6EE7B7' },
  active: { bg: '#DBEAFE', text: '#1E40AF', border: '#93C5FD' },
  completed: { bg: '#F3F4F6', text: '#6B7280', border: '#D1D5DB' },
  good_to_go: { bg: '#D1FAE5', text: '#065F46', border: '#6EE7B7' },
  needs_people: { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' },
  at_risk: { bg: '#FEE2E2', text: '#991B1B', border: '#FECACA' },
  canceled: { bg: '#F3F4F6', text: '#6B7280', border: '#D1D5DB' },
};

const LIVE_STATE_LABELS = {
  good_to_go: 'Good to go',
  needs_people: 'Needs people',
  at_risk: 'At risk',
  completed: 'Completed',
  canceled: 'Canceled',
};

function deriveLiveStateDetail(activity) {
  const status = activity.lifecycle_status || 'idea';
  if (status === 'completed') return 'completed';
  if (status === 'canceled') return 'canceled';
  const goingCount = activity.participant_count ?? activity.participants?.length ?? 0;
  const startTime = activity.start_time ? new Date(activity.start_time) : null;
  const now = new Date();
  const hoursUntil = startTime ? (startTime - now) / (1000 * 60 * 60) : Infinity;
  if (hoursUntil < 4 && hoursUntil > 0 && goingCount < 2) return 'at_risk';
  if (goingCount >= 3) return 'good_to_go';
  return 'needs_people';
}

export default function ActivityDetail() {
  const { activityId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signals, setSignals] = useState([]);
  const [participantSummary, setParticipantSummary] = useState(null);
  const [mySignal, setMySignal] = useState(null);
  const [signalingId, setSignalingId] = useState(null);
  const [myContribution, setMyContribution] = useState({ ride: null, equipment: null });
  const [contributionCounts, setContributionCounts] = useState({ can_drive: 0, need_ride: 0, bringing: 0, needing: 0 });
  const [savingContribution, setSavingContribution] = useState(false);
  const [activityDocs, setActivityDocs] = useState([]);
  const [scutaEntry, setScutaEntry] = useState(null);
  const [creatingEntry, setCreatingEntry] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [showBroadcastForm, setShowBroadcastForm] = useState(false);
  const [broadcastType, setBroadcastType] = useState('on');
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [broadcastNewLocation, setBroadcastNewLocation] = useState('');
  const [submittingBroadcast, setSubmittingBroadcast] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // "I'm running late" — one-way logistics signal. Minimal state: window
  // availability, whether this participant has sent, and a host-only flag.
  const [runningLate, setRunningLate] = useState({ window_active: false, self_sent: false, someone_running_late: false });
  const [sendingLate, setSendingLate] = useState(false);
  // Continuation flows — Wave 1 additions
  const [keepGoingBusy, setKeepGoingBusy] = useState(false);
  const [duplicateBusy, setDuplicateBusy] = useState(false);
  const [hasRevisited, setHasRevisited] = useState(false);
  const [showPostJoinHelp, setShowPostJoinHelp] = useState(false);
  const [myActivityCount, setMyActivityCount] = useState(0);
  // Source-group support state — only fetched when activity has source_id.
  const [sourceGroup, setSourceGroup] = useState(null);

  useEffect(() => {
    loadActivity();
    loadSignals();
    loadDocs();
    loadBroadcasts();
    loadRunningLate();
  }, [activityId]);

  useEffect(() => {
    if (activity?.lifecycle_status === 'completed') loadScutaEntry();
  }, [activity?.lifecycle_status]);

  const loadActivity = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/api/activities/${activityId}`, { headers });
      setActivity(res.data);
      setMySignal(res.data.my_signal || null);
      setMyContribution(res.data.my_contribution || { ride: null, equipment: null });
      setContributionCounts(res.data.contribution_counts || { can_drive: 0, need_ride: 0, bringing: 0, needing: 0 });
      setLoadError(false);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const loadSignals = async () => {
    try {
      const res = await axios.get(`${API}/api/activities/participation-signals`);
      setSignals(res.data.signals || []);
    } catch { /* ignore */ }
  };

  const loadDocs = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/api/documents/activity/${activityId}`, { headers });
      setActivityDocs(res.data.documents || []);
    } catch { /* ignore */ }
  };

  const loadScutaEntry = async () => {
    try {
      const res = await fetch(`${API}/api/scuta/entries/for-activity/${activityId}`);
      if (res.ok) setScutaEntry(await res.json());
    } catch { /* no entry yet */ }
  };

  const loadBroadcasts = async () => {
    try {
      const res = await axios.get(`${API}/api/activities/${activityId}/broadcasts`);
      setBroadcasts(res.data.broadcasts || []);
    } catch { /* ignore */ }
  };

  const loadRunningLate = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/api/activities/${activityId}/running-late`, { headers });
      setRunningLate({
        window_active: Boolean(res.data?.window_active),
        self_sent: Boolean(res.data?.self_sent),
        someone_running_late: Boolean(res.data?.someone_running_late),
      });
    } catch {
      setRunningLate({ window_active: false, self_sent: false, someone_running_late: false });
    }
  };

  const handleRunningLate = async () => {
    if (sendingLate || runningLate.self_sent) return;
    setSendingLate(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/activities/${activityId}/running-late`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setRunningLate(prev => ({ ...prev, self_sent: true }));
    } catch {
      // Quiet failure — no toast, no retry (signal, not conversation)
    } finally {
      setSendingLate(false);
    }
  };

  // ─── Continuation flows ───
  // Track revisit signal locally — second view of the same activity by the
  // same browser elevates the "Keep this going" line. Quiet, non-cumulative.
  useEffect(() => {
    if (!activityId) return;
    try {
      const key = `lk_act_visit_${activityId}`;
      const seen = localStorage.getItem(key);
      if (seen) {
        setHasRevisited(true);
      } else {
        localStorage.setItem(key, '1');
      }
    } catch { /* private mode — fine, just don't elevate */ }
  }, [activityId]);

  // Pull the user's activity count once — "another activity by same user"
  // is one of the elevation triggers.
  useEffect(() => {
    if (!user) { setMyActivityCount(0); return; }
    const token = localStorage.getItem('token');
    if (!token) return;
    let mounted = true;
    axios.get(`${API}/api/activities/my-activities`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!mounted) return;
        const list = res.data?.activities || res.data || [];
        setMyActivityCount(Array.isArray(list) ? list.length : 0);
      })
      .catch(() => { /* quiet */ });
    return () => { mounted = false; };
  }, [user]);

  // When the activity belongs to a group, fetch a tiny slice of the group's
  // public profile so we can render the Support layer line if the group is
  // Commons. No counts, just funding state and supporter name.
  useEffect(() => {
    const sid = activity?.source_id;
    if (!sid || activity?.source_type !== 'group') { setSourceGroup(null); return; }
    let mounted = true;
    axios.get(`${API}/api/groups/${sid}/profile`)
      .then(res => {
        if (!mounted) return;
        const g = res.data || {};
        setSourceGroup({
          group_id: sid,
          is_commons: Boolean(g.is_commons),
          commons_funding: g.commons_funding || null,
          supported_by_name: g.supported_by_name || null,
        });
      })
      .catch(() => { if (mounted) setSourceGroup(null); });
    return () => { mounted = false; };
  }, [activity?.source_id, activity?.source_type]);

  // One-time post-join help line — shown exactly once after the user signals
  // "in" on this activity, then suppressed forever for this user+activity.
  useEffect(() => {
    if (!user || !activityId) { setShowPostJoinHelp(false); return; }
    if (mySignal !== 'in') { setShowPostJoinHelp(false); return; }
    if (activity?.is_host) { setShowPostJoinHelp(false); return; }
    if (!activity?.source_id || activity.source_type !== 'group') {
      setShowPostJoinHelp(false); return;
    }
    try {
      const key = `lk_post_join_help_${activityId}_${user.user_id}`;
      if (localStorage.getItem(key)) {
        setShowPostJoinHelp(false);
        return;
      }
      setShowPostJoinHelp(true);
      localStorage.setItem(key, '1');
    } catch { /* private mode — never mind */ }
  }, [user, activityId, mySignal, activity?.is_host, activity?.source_id, activity?.source_type]);

  const handleKeepGoing = async () => {
    if (keepGoingBusy) return;
    setKeepGoingBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/activities/${activityId}/keep-going`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const groupId = res.data?.group_id;
      if (groupId) navigate(`/group/${groupId}`);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (typeof msg === 'string') toast.error(msg);
    } finally {
      setKeepGoingBusy(false);
    }
  };

  const handleDoThisAgain = async () => {
    if (duplicateBusy) return;
    setDuplicateBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/activities/${activityId}/duplicate`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const draft = res.data?.draft;
      if (draft) {
        // Hand off to the create flow via sessionStorage; ActivityCreate
        // will hydrate from this on mount.
        sessionStorage.setItem('lk_duplicate_draft', JSON.stringify(draft));
        navigate('/activities/create?from=duplicate');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (typeof msg === 'string') toast.error(msg);
    } finally {
      setDuplicateBusy(false);
    }
  };

  const handleBroadcast = async () => {
    setSubmittingBroadcast(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { type: broadcastType, message: broadcastMsg };
      if (broadcastType === 'location_change' && broadcastNewLocation.trim()) {
        payload.new_location = broadcastNewLocation;
      }
      await axios.post(`${API}/api/activities/${activityId}/broadcast`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Update posted: ${broadcastType}`);
      setShowBroadcastForm(false);
      setBroadcastMsg('');
      setBroadcastNewLocation('');
      loadBroadcasts();
      if (broadcastType === 'canceled') loadActivity();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post update');
    } finally {
      setSubmittingBroadcast(false);
    }
  };

  const handleCreateScutaEntry = async () => {
    setCreatingEntry(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/scuta/entries/from-activity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ activity_id: activityId }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('SCUTA record created');
        loadScutaEntry();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create entry');
      }
    } catch {
      toast.error('Failed to create SCUTA record');
    } finally {
      setCreatingEntry(false);
    }
  };

  const loadParticipants = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/activities/${activityId}/participants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setParticipantSummary(res.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (activity) loadParticipants();
  }, [activity]);

  const handleSignal = async (signalId) => {
    if (!user) {
      toast('Create a free account to join', { action: { label: 'Sign up', onClick: () => navigate('/auth?mode=signup') } });
      return;
    }
    setSignalingId(signalId);
    try {
      const token = localStorage.getItem('token');
      if (mySignal === signalId) {
        await axios.delete(`${API}/api/activities/${activityId}/participate`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMySignal(null);
        toast.success('Participation removed');
      } else {
        await axios.post(`${API}/api/activities/${activityId}/participate`,
          { signal_id: signalId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMySignal(signalId);
        const label = signals.find(s => s.id === signalId)?.label || signalId;
        toast.success(`Marked as "${label}"`);
      }
      loadParticipants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSignalingId(null);
    }
  };

  const handleContribution = async (dimension, value) => {
    if (!user) return;
    // Toggle: if selecting the same value, clear it
    const currentRide = myContribution.ride;
    const currentEquip = myContribution.equipment;
    const next = {
      ride: dimension === 'ride' ? (currentRide === value ? null : value) : currentRide,
      equipment: dimension === 'equipment' ? (currentEquip === value ? null : value) : currentEquip,
    };
    setSavingContribution(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/activities/${activityId}/contribution`,
        next,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMyContribution(res.data.my_contribution);
      setContributionCounts(res.data.contribution_counts);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to update');
    } finally {
      setSavingContribution(false);
    }
  };

  const handleLifecycleAdvance = async (newStatus) => {
    if (newStatus === 'completed') {
      if (!window.confirm('Mark this activity as completed?\n\nThis confirms the activity actually occurred. Participation will be frozen and this action is final.')) {
        return;
      }
    }
    try {
      const token = localStorage.getItem('token');
      const payload = { status: newStatus };
      if (newStatus === 'completed') {
        payload.confirm_completion = true;
      }
      await axios.put(`${API}/api/activities/${activityId}/lifecycle`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status updated to ${newStatus}`);
      loadActivity();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this activity? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/activities/${activityId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Activity deleted');
      navigate('/base-camp');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--lk-sage)' }} />
      </div>
    );
  }

  if (!activity) {
    if (loadError) {
      return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
          <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
            <div className="max-w-2xl mx-auto px-4 flex items-center h-14 gap-3">
              <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:opacity-70" aria-label="Back">
                <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
              </button>
              <span className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Activity</span>
            </div>
          </header>
          <main className="max-w-2xl mx-auto px-4 py-8">
            <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }} data-testid="activity-load-error-whisper">
              Couldn't load right now. Try again in a moment.
            </p>
          </main>
        </div>
      );
    }
    return null;
  }

  const isHost = activity.is_host;
  const status = activity.lifecycle_status || 'idea';
  const liveState = deriveLiveStateDetail(activity);
  const statusInfo = STATUS_COLORS[liveState] || STATUS_COLORS.needs_people;
  const stateLabel = LIVE_STATE_LABELS[liveState] || liveState;
  const stateData = {
    idea: { label: stateLabel, next: 'confirmed', nextLabel: 'Confirm' },
    confirmed: { label: stateLabel, next: 'active', nextLabel: 'Start', prevLabel: 'Back to Idea', prev: 'idea' },
    active: { label: stateLabel, next: 'completed', nextLabel: 'Complete' },
    completed: { label: 'Completed' }
  }[status] || { label: stateLabel };

  const startTime = activity.start_time ? new Date(activity.start_time) : null;
  const endTime = activity.end_time ? new Date(activity.end_time) : null;

  return (
    <div className="min-h-screen pb-32" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:opacity-70" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <span className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Activity</span>
          </div>
          {isHost && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/activities/${activityId}/edit`)}
                className="p-2 rounded-full hover:opacity-70"
                data-testid="edit-activity-btn"
              >
                <Edit2 className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
              </button>
              <button
                onClick={handleDelete}
                className="p-2 rounded-full hover:opacity-70"
                data-testid="delete-activity-btn"
              >
                <Trash2 className="w-4 h-4" style={{ color: 'var(--lk-terra)' }} />
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-5">

        {/* Post-create share nudge — one-shot, dismissable, user-initiated.
            Triggered when ActivityCreate sets sessionStorage['lk_just_created_activity']. */}
        <JustCreatedShareNudge
          activityId={activity.activity_id}
          onShare={() => handleShare()}
          onCopyLink={() => handleCopyLink()}
          onFlyer={() => { setShowFlyer(true); setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 300); }}
        />

        {/* Hero image — single source of truth: activity.image_url (same as Card & Flyer) */}
        <div
          className="w-full aspect-[16/9] rounded-2xl overflow-hidden"
          style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}
          data-testid="activity-hero"
        >
          {activity.image_url ? (
            <img
              src={activity.image_url}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
              data-testid="activity-hero-img"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, var(--fy-surface-muted) 0%, #E4EADD 100%)' }}
            >
              <MapPin className="w-8 h-8" style={{ color: 'var(--lk-sage)', opacity: 0.35 }} />
            </div>
          )}
        </div>

        {/* Lifecycle Badge + Title */}
        <div>
          <span
            className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3"
            style={{ backgroundColor: statusInfo.bg, color: statusInfo.text, border: `1px solid ${statusInfo.border}` }}
            data-testid="lifecycle-badge"
          >
            {stateData.label}
          </span>
          {activity.activity_type === 'virtual' && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mb-2"
              style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }}
              data-testid="virtual-badge"
            >
              <LinkIcon className="w-3 h-3" /> Virtual
            </span>
          )}
          <h1 className="text-2xl font-bold" style={{ color: 'var(--fy-text-primary)' }} data-testid="activity-title">
            {activity.title}
          </h1>
          {(activity.visibility === 'public' || activity.visibility === 'private') && (
            <span
              className="inline-flex items-center gap-1 mt-2 text-xs"
              style={{ color: 'var(--fy-text-secondary)' }}
              data-testid="activity-visibility-pill"
            >
              {activity.visibility === 'public' ? (
                <>
                  <Globe className="w-3 h-3" />
                  Public
                </>
              ) : (
                <>
                  <Lock className="w-3 h-3" />
                  Private
                </>
              )}
            </span>
          )}
        </div>

        {/* Details Card — LOCKED STRUCTURE: Time → Location → Host → Participation */}
        <Card className="p-4 space-y-3" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
          {/* Date & Time (always first) */}
          {startTime && (
            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--lk-secondary-orange)' }} />
              <div>
                <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>When</p>
                <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                  {startTime.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  {' at '}
                  {startTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  {endTime && (
                    <span style={{ color: 'var(--fy-text-secondary)' }}>
                      {' - '}{endTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Location (always second) — virtual activities show a Join link instead */}
          {activity.activity_type === 'virtual' ? (
            <div className="flex items-center gap-3" data-testid="activity-virtual-row">
              <LinkIcon className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--lk-sage)' }} />
              <div className="flex-1 min-w-0">
                <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>Virtual · Join link</p>
                {activity.my_signal === 'in' || isHost ? (
                  <a
                    href={activity.join_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium break-all hover:underline"
                    style={{ color: 'var(--lk-sage)' }}
                    data-testid="activity-join-link"
                  >
                    {activity.join_link}
                  </a>
                ) : (
                  <p className="text-sm" style={{ color: 'var(--fy-text-disabled)' }}>
                    Link is shown after you join.
                  </p>
                )}
              </div>
            </div>
          ) : activity.location && (() => {
            const locText = typeof activity.location === 'string' ? activity.location : (activity.location?.name || '');
            const kind = classifyLocation(locText);
            if (kind === 'private') {
              return (
                <div className="flex items-center gap-3" data-testid="activity-location-row" data-location-kind="private">
                  <Lock className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
                  <div>
                    <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>Location</p>
                    <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                      Location shared after joining
                    </p>
                  </div>
                </div>
              );
            }
            const label = kind === 'general' ? `${locText} area` : locText;
            const mapHref = kind === 'exact'
              ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locText)}`
              : null;
            return (
              <div className="flex items-center gap-3" data-testid="activity-location-row" data-location-kind={kind}>
                <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--lk-earth-brown)' }} />
                <div className="min-w-0">
                  <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>Location</p>
                  {mapHref ? (
                    <a
                      href={mapHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium hover:underline"
                      style={{ color: 'var(--fy-text-primary)' }}
                      data-testid="activity-location-map-link"
                    >
                      {label}
                    </a>
                  ) : (
                    <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                      {label}
                    </p>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Host */}
          {(activity.host_name || activity.created_by_name || (activity.cohosts && activity.cohosts.length > 0)) && (() => {
            const primary = activity.host_name || activity.created_by_name || '';
            const cohosts = Array.isArray(activity.cohosts) ? activity.cohosts.filter(Boolean) : [];
            const others = cohosts
              .map(c => (typeof c === 'string' ? c : (c.name || c.host_name || '')))
              .filter(Boolean);
            const showWith = others.length > 0;
            return (
              <div className="flex items-center gap-3" data-testid="activity-host-row">
                <User className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--lk-sage)' }} />
                <div>
                  <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                    {showWith ? 'Hosts' : 'Led by'}
                  </p>
                  <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                    {primary}
                    {showWith && (
                      <>
                        <span style={{ color: 'var(--fy-text-secondary)' }}> · with </span>
                        {others.join(', ')}
                      </>
                    )}
                  </p>
                </div>
              </div>
            );
          })()}
        </Card>

        {/* Sponsor badge — calm, explicit, secondary */}
        <SponsorBadge activityId={activityId} placementType="activity_page" />

        {/* Description */}
        {activity.description && (
          <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
            <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: 'var(--fy-text-primary)' }} data-testid="activity-description">
              {activity.description}
            </p>
          </Card>
        )}

        {/* What to know — short, practical notes */}
        {activity.what_to_know && (
          <Card
            className="p-4"
            style={{
              backgroundColor: 'var(--fy-surface-card)',
              border: '1px solid var(--fy-border-subtle)',
              borderLeft: '3px solid var(--lk-sage)',
            }}
            data-testid="activity-what-to-know"
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1.5" style={{ color: 'var(--lk-sage)' }}>
              What to know
            </p>
            <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: 'var(--fy-text-primary)' }}>
              {activity.what_to_know}
            </p>
          </Card>
        )}

        {/* Lifecycle Controls (Host only) */}
        {isHost && status !== 'completed' && (
          <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
            <p className="text-xs font-medium mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
              Advance Status
            </p>
            <div className="flex gap-2">
              {stateData.prev && (
                <Button
                  onClick={() => handleLifecycleAdvance(stateData.prev)}
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  data-testid="lifecycle-prev-btn"
                >
                  {stateData.prevLabel}
                </Button>
              )}
              {stateData.next && (
                <Button
                  onClick={() => handleLifecycleAdvance(stateData.next)}
                  size="sm"
                  className="rounded-full"
                  style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}
                  data-testid="lifecycle-next-btn"
                >
                  <ChevronRight className="w-4 h-4 mr-1" />
                  {stateData.nextLabel}
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Coverage info — only when meaningful */}
        {participantSummary && (() => {
          const sigs = {};
          (participantSummary.signal_summary || []).forEach(s => { sigs[s.signal_id] = s.count; });
          const needRide = sigs['need_ride'] || 0;
          const canDrive = sigs['can_drive'] || 0;
          const needEquip = sigs['need_equipment'] || 0;
          const bringingEquip = sigs['bringing_equipment'] || 0;
          const needVol = sigs['need_volunteers'] || 0;
          const needTools = sigs['need_tools'] || 0;
          const needSupplies = sigs['need_supplies'] || 0;
          const lines = [];
          if (needRide > 0 && canDrive === 0) lines.push('Driver needed');
          else if (needRide > 0 && canDrive > 0) lines.push(`${canDrive} driving, ${needRide} need ride`);
          if (bringingEquip > 0 && needEquip === 0) lines.push('Equipment covered');
          else if (needEquip > 0 && bringingEquip === 0) lines.push('Equipment needed');
          if (needVol > 0) lines.push(`${needVol} need volunteers`);
          if (needTools > 0) lines.push('Tools needed');
          if (needSupplies > 0) lines.push('Supplies needed');
          return lines.length > 0 ? (
            <p className="text-xs px-1" style={{ color: 'var(--fy-text-disabled)' }} data-testid="detail-coverage">{lines.join(' · ')}</p>
          ) : null;
        })()}

        {/* Broadcast Updates (visible to everyone) */}
        {broadcasts.length > 0 && (
          <Card className="p-4 space-y-2" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }} data-testid="broadcast-updates">
            <p className="text-xs font-semibold mb-1" style={{ color: 'var(--fy-text-secondary)' }}>Updates</p>
            {broadcasts.slice().reverse().map((b, i) => {
              const typeStyles = {
                on: { color: '#059669', label: 'On' },
                delayed: { color: '#D97706', label: 'Delayed' },
                location_change: { color: '#3B82F6', label: 'Location changed' },
                canceled: { color: '#DC2626', label: 'Canceled' },
              };
              const s = typeStyles[b.type] || { color: 'var(--fy-text-secondary)', label: b.type };
              return (
                <div key={i} className="flex items-start gap-2 py-1.5" style={{ borderBottom: i < broadcasts.length - 1 ? '1px solid var(--fy-border-subtle)' : 'none' }}>
                  <Radio className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: s.color }} />
                  <div>
                    <span className="text-xs font-semibold" style={{ color: s.color }}>{s.label}</span>
                    {b.message && <p className="text-xs mt-0.5" style={{ color: 'var(--fy-text-primary)' }}>{b.message}</p>}
                    {b.new_location && <p className="text-xs mt-0.5" style={{ color: 'var(--fy-text-secondary)' }}>New location: {b.new_location}</p>}
                    {b.posted_at && <p className="text-[10px] mt-0.5" style={{ color: 'var(--fy-text-disabled)' }}>{new Date(b.posted_at).toLocaleString()}</p>}
                  </div>
                </div>
              );
            })}
          </Card>
        )}

        {/* Host Broadcast Form */}
        {isHost && status !== 'completed' && (
          <div>
            {!showBroadcastForm ? (
              <button
                onClick={() => setShowBroadcastForm(true)}
                className="flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-full"
                style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-secondary)' }}
                data-testid="broadcast-open-btn"
              >
                <Radio className="w-3.5 h-3.5" />
                Post an update
              </button>
            ) : (
              <Card className="p-4 space-y-3" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }} data-testid="broadcast-form">
                <p className="text-xs font-semibold" style={{ color: 'var(--fy-text-secondary)' }}>Post Update</p>
                <div className="flex gap-1.5 flex-wrap">
                  {[
                    { id: 'on', label: 'On', color: '#059669' },
                    { id: 'delayed', label: 'Delayed', color: '#D97706' },
                    { id: 'location_change', label: 'Location change', color: '#3B82F6' },
                    { id: 'canceled', label: 'Canceled', color: '#DC2626' },
                  ].map(bt => (
                    <button
                      key={bt.id}
                      onClick={() => setBroadcastType(bt.id)}
                      className="px-3 py-1 rounded-full text-xs font-medium transition-colors"
                      style={{
                        backgroundColor: broadcastType === bt.id ? `${bt.color}15` : 'var(--fy-surface-muted)',
                        color: broadcastType === bt.id ? bt.color : 'var(--fy-text-secondary)',
                        border: `1.5px solid ${broadcastType === bt.id ? bt.color : 'var(--fy-border-subtle)'}`,
                      }}
                      data-testid={`broadcast-type-${bt.id}`}
                    >
                      {bt.label}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={broadcastMsg}
                  onChange={e => setBroadcastMsg(e.target.value)}
                  placeholder="Optional note..."
                  maxLength={200}
                  className="w-full px-3 py-2 rounded-lg text-xs outline-none"
                  style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
                  data-testid="broadcast-message-input"
                />
                {broadcastType === 'location_change' && (
                  <input
                    type="text"
                    value={broadcastNewLocation}
                    onChange={e => setBroadcastNewLocation(e.target.value)}
                    placeholder="New location..."
                    className="w-full px-3 py-2 rounded-lg text-xs outline-none"
                    style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
                    data-testid="broadcast-new-location-input"
                  />
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={handleBroadcast}
                    disabled={submittingBroadcast}
                    size="sm"
                    className="rounded-full text-xs"
                    style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }}
                    data-testid="broadcast-submit-btn"
                  >
                    {submittingBroadcast ? 'Posting...' : 'Post update'}
                  </Button>
                  <Button
                    onClick={() => setShowBroadcastForm(false)}
                    variant="outline"
                    size="sm"
                    className="rounded-full text-xs"
                    data-testid="broadcast-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Participation Signals */}
        {status !== 'completed' && (
          <div>
            <h2 className="text-sm font-semibold mb-3" style={{ color: 'var(--fy-text-primary)' }}>
              How would you like to participate?
            </h2>
            <div className="grid grid-cols-3 gap-2" data-testid="participation-signals">
              {signals
                .filter(signal => {
                  // Community signals only show on community-category activities
                  const communitySignals = ['need_volunteers', 'need_tools', 'need_supplies'];
                  if (communitySignals.includes(signal.id)) {
                    return activity.category === 'community' || activity.category === 'serve';
                  }
                  return true;
                })
                .map(signal => {
                const Icon = SIGNAL_ICONS[signal.id] || Check;
                const color = SIGNAL_COLORS[signal.id] || 'var(--lk-sage)';
                const isActive = mySignal === signal.id;
                const count = participantSummary?.signal_summary?.find(s => s.signal_id === signal.id)?.count || 0;

                return (
                  <button
                    key={signal.id}
                    onClick={() => handleSignal(signal.id)}
                    disabled={signalingId === signal.id}
                    className="p-3 rounded-xl text-center transition-all"
                    style={{
                      backgroundColor: isActive ? `${color}15` : 'var(--fy-surface-card)',
                      border: `2px solid ${isActive ? color : 'var(--fy-border-subtle)'}`,
                      opacity: signalingId === signal.id ? 0.6 : 1
                    }}
                    data-testid={`signal-${signal.id}`}
                  >
                    <Icon className="w-5 h-5 mx-auto mb-1" style={{ color: isActive ? color : 'var(--fy-text-secondary)' }} />
                    <p className="text-xs font-medium" style={{ color: isActive ? color : 'var(--fy-text-primary)' }}>
                      {signal.label}
                    </p>
                    {count > 0 && isHost && (
                      <p className="text-xs mt-0.5" style={{ color: 'var(--fy-text-disabled)' }} data-testid={`signal-count-${signal.id}`}>
                        {count}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Contribution — rides + equipment signals (only when user has joined) */}
        {user && (mySignal === 'in' || activity?.is_host) && (activity?.lifecycle_status !== 'completed') && (
          <Card
            className="p-4"
            style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
            data-testid="contribution-panel"
          >
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--fy-text-disabled)' }}>
              Contribute
            </p>

            {/* Rides row */}
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-2">
                <Car className="w-3.5 h-3.5" style={{ color: 'var(--fy-text-secondary)' }} />
                <span className="text-xs font-medium" style={{ color: 'var(--fy-text-secondary)' }}>Rides</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {[
                  { id: 'can_drive', label: 'Can drive' },
                  { id: 'need_ride', label: 'Need ride' },
                ].map(opt => {
                  const active = myContribution.ride === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => handleContribution('ride', opt.id)}
                      disabled={savingContribution}
                      className="px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
                      style={{
                        backgroundColor: active ? 'var(--lk-sage)' : 'var(--fy-surface-muted)',
                        color: active ? '#fff' : 'var(--fy-text-primary)',
                        border: `1px solid ${active ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`,
                      }}
                      data-testid={`contrib-ride-${opt.id}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Equipment row */}
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-3.5 h-3.5" style={{ color: 'var(--fy-text-secondary)' }} />
                <span className="text-xs font-medium" style={{ color: 'var(--fy-text-secondary)' }}>Equipment</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {[
                  { id: 'bringing', label: 'Bringing equipment' },
                  { id: 'needing', label: 'Need equipment' },
                ].map(opt => {
                  const active = myContribution.equipment === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => handleContribution('equipment', opt.id)}
                      disabled={savingContribution}
                      className="px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
                      style={{
                        backgroundColor: active ? 'var(--lk-sage)' : 'var(--fy-surface-muted)',
                        color: active ? '#fff' : 'var(--fy-text-primary)',
                        border: `1px solid ${active ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`,
                      }}
                      data-testid={`contrib-equip-${opt.id}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Aggregated counts — no user lists */}
            {(() => {
              const parts = [];
              if (contributionCounts.can_drive > 0) parts.push(`${contributionCounts.can_drive} can drive`);
              if (contributionCounts.need_ride > 0) parts.push(`${contributionCounts.need_ride} need${contributionCounts.need_ride === 1 ? 's' : ''} ride`);
              if (contributionCounts.bringing > 0) parts.push(`${contributionCounts.bringing} bringing equipment`);
              if (contributionCounts.needing > 0) parts.push(`${contributionCounts.needing} need${contributionCounts.needing === 1 ? 's' : ''} equipment`);
              if (parts.length === 0) {
                return (
                  <p className="text-[11px]" style={{ color: 'var(--fy-text-disabled)' }} data-testid="contrib-micro-line">
                    Coordination happens automatically.
                  </p>
                );
              }
              // Derive a lightweight covered/needed line
              const rideGap = contributionCounts.need_ride - contributionCounts.can_drive;
              const eqGap = contributionCounts.needing - contributionCounts.bringing;
              let micro = 'Coordination happens automatically.';
              if (rideGap > 0) micro = `Still need ${rideGap} more ${rideGap === 1 ? 'ride' : 'rides'}.`;
              else if (contributionCounts.need_ride > 0 && rideGap <= 0) micro = 'Looks like rides are covered.';
              else if (eqGap > 0) micro = `Still need ${eqGap} more ${eqGap === 1 ? 'item' : 'items'}.`;
              return (
                <>
                  <p className="text-[11px]" style={{ color: 'var(--fy-text-secondary)' }} data-testid="contrib-counts-line">
                    {parts.join(' · ')}
                  </p>
                  <p className="text-[11px] mt-0.5" style={{ color: 'var(--fy-text-disabled)' }} data-testid="contrib-micro-line">
                    {micro}
                  </p>
                </>
              );
            })()}
          </Card>
        )}

        {/* "I'm running late" — one-way logistics signal. Only renders during
            the active window (shortly before → shortly after start). Host
            sees a single inline "Someone is running late" line when any
            participant has signaled. No names, no counts, no threads. */}
        {user && !isHost && mySignal === 'in' && runningLate.window_active && (
          <div className="px-1" data-testid="running-late-participant">
            <button
              type="button"
              onClick={handleRunningLate}
              disabled={runningLate.self_sent || sendingLate}
              className="text-sm font-medium disabled:opacity-60"
              style={{
                color: runningLate.self_sent ? 'var(--fy-text-disabled)' : 'var(--lk-sage)',
                cursor: runningLate.self_sent ? 'default' : 'pointer',
              }}
              data-testid="running-late-button"
            >
              {runningLate.self_sent ? 'Sent' : "I'm running late"}
            </button>
          </div>
        )}
        {user && isHost && runningLate.someone_running_late && (
          <p
            className="text-sm px-1"
            style={{ color: 'var(--fy-text-secondary)' }}
            data-testid="running-late-host-line"
          >
            Someone is running late.
          </p>
        )}

        {/* Participants — avatars always; explicit count host-only */}
        {participantSummary && participantSummary.total_participants > 0 && (
          <div className="flex items-center gap-2 px-1" data-testid="participant-summary">
            {participantSummary.participant_handles && (
              <div className="flex -space-x-2">
                {participantSummary.participant_handles.slice(0, 5).map((p, i) => (
                  <div
                    key={i}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold"
                    style={{ backgroundColor: 'var(--lk-sage-light)', color: 'var(--lk-sage)', border: '2px solid var(--fy-background-primary)', zIndex: 5 - i }}
                  >
                    {(p.handle || '?')[0].toUpperCase()}
                  </div>
                ))}
              </div>
            )}
            {isHost && (
              <p
                className="text-sm font-medium"
                style={{ color: 'var(--fy-text-primary)' }}
                data-testid="participant-count-host"
              >
                {participantSummary.total_participants} going
              </p>
            )}
          </div>
        )}

        {/* Presence line — host only (uses count thresholds, must not show to non-hosts) */}
        {isHost && participantSummary && participantSummary.total_participants > 0 && (
          <p className="text-xs px-1" style={{ color: 'var(--fy-text-disabled)' }} data-testid="presence-line">
            {participantSummary.total_participants >= 5
              ? 'People are planning to be there.'
              : 'A few people are already going.'}
          </p>
        )}

        {/* Sponsor support line — tappable */}
        <div
          className="py-1.5 px-1 cursor-pointer hover:opacity-80"
          onClick={() => navigate('/sponsors/sponsor_community_foundation')}
          data-testid="sponsor-support"
        >
          <p className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
            Made possible by <span className="font-medium" style={{ color: 'var(--fy-text-secondary)' }}>Community Foundation</span>
          </p>
        </div>

        {/* ─── SHARE & FLYER SYSTEM ─── */}
        <SharePanel activityId={activity.activity_id} title={activity.title} imageUrl={activity.image_url} />

        {/* Completed state */}
        {status === 'completed' && (
          <Card className="p-4 text-center" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
            <Check className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--lk-sage)' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>This activity has been completed.</p>
            <p className="text-xs mt-1" style={{ color: 'var(--fy-text-secondary)' }}>Thank you to everyone who participated.</p>
          </Card>
        )}

        {/* SCUTA Entry — Archive record for completed activities */}
        {status === 'completed' && isHost && !scutaEntry && (
          <Card
            className="p-4"
            style={{ backgroundColor: '#111820', border: '1px solid #1E2936' }}
            data-testid="scuta-entry-prompt"
          >
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#C2A45E' }} />
              <div className="flex-1">
                <p className="text-sm font-medium" style={{ color: '#E6E9EE' }}>Archive this activity in SCUTA</p>
                <p className="text-xs mt-1 mb-3" style={{ color: '#9AA3AF' }}>
                  Create a permanent record of this activity for the community timeline.
                </p>
                <Button
                  onClick={handleCreateScutaEntry}
                  disabled={creatingEntry}
                  size="sm"
                  className="rounded-full text-xs"
                  style={{ backgroundColor: '#C2A45E', color: '#0B0F14' }}
                  data-testid="create-scuta-entry-btn"
                >
                  {creatingEntry ? 'Creating...' : 'Create SCUTA Record'}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* SCUTA Entry exists — show link */}
        {status === 'completed' && scutaEntry && (
          <Card
            className="p-4 cursor-pointer hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#111820', border: '1px solid #1E2936' }}
            onClick={() => navigate(`/scuta/entries/${scutaEntry.entry_id}`)}
            data-testid="scuta-entry-link"
          >
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 flex-shrink-0" style={{ color: '#C2A45E' }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium" style={{ color: '#E6E9EE' }}>SCUTA Record</p>
                <p className="text-xs" style={{ color: '#9AA3AF' }}>
                  {scutaEntry.status === 'draft' ? 'Draft — not yet published' :
                   scutaEntry.status === 'pending' ? 'Pending review' :
                   scutaEntry.status === 'approved' ? 'Published in archive' : scutaEntry.status}
                </p>
              </div>
              <ChevronRight className="w-4 h-4" style={{ color: '#9AA3AF' }} />
            </div>
          </Card>
        )}

        {/* Documents / Waivers */}
        {activityDocs.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold mb-2" style={{ color: 'var(--fy-text-primary)' }}>
              Documents
            </h2>
            <div className="space-y-2" data-testid="activity-documents">
              {activityDocs.map(doc => (
                <Card
                  key={doc.document_id}
                  className="p-3 cursor-pointer"
                  onClick={() => navigate(`/documents/${doc.document_id}`)}
                  style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 flex-shrink-0" style={{ color: doc.acknowledged ? 'var(--lk-sage)' : '#F59E0B' }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--fy-text-primary)' }}>{doc.title}</p>
                      <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>{doc.document_type?.replace('_', ' ')}</p>
                    </div>
                    {doc.requires_acknowledgement && (
                      doc.acknowledged ? (
                        <Check className="w-4 h-4" style={{ color: 'var(--lk-sage)' }} />
                      ) : (
                        <AlertCircle className="w-4 h-4" style={{ color: '#F59E0B' }} />
                      )
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Host tools — quiet in-page controls. Only renders when user is the host. */}
        <HostTools
          activity={activity}
          hasEnded={status === 'completed' || (endTime && endTime < new Date())}
          onChanged={() => { /* ActivityDetail re-fetches on focus; no-op here. */ }}
        />

        {/* Activity chat — participants + host only; fades after the activity ends. */}
        <ActivityChat
          activityId={activity.activity_id}
          isParticipant={!!activity.my_signal}
          isHost={isHost}
        />

        {/* Post-activity layer (Photos). Renders only when activity has ended. */}
        <PostActivityPanel
          activityId={activity.activity_id}
          hasEnded={status === 'completed' || (endTime && endTime < new Date())}
          isHost={isHost}
          isParticipant={!!activity.my_signal}
        />

        {/* Support layer (Activity → Group) — calm, single line.
            Visible to anyone when the activity belongs to a Commons group. */}
        {sourceGroup && sourceGroup.is_commons && sourceGroup.supported_by_name && (
          <button
            type="button"
            onClick={() => navigate(`/group/${sourceGroup.group_id}`)}
            className="text-sm font-medium hover:underline self-start"
            style={{ color: '#6D5238' }}
            data-testid="activity-support-line"
          >
            This group is kept going by {sourceGroup.supported_by_name}
          </button>
        )}

        {/* ─── Continuation lines (creator + participant) ───
            "Do this again" + "Keep this going" — creator-only, quiet by
            default, elevated when revisit / edited / repeat-creator / has
            participants. "This could continue" — shown to non-creators when
            the activity already belongs to a group. Post-join help — shown
            once to participants. All calm, all non-CTA. */}
        {isHost && (() => {
          const total = participantSummary?.total_participants || 0;
          const wasEdited = activity.updated_at && activity.created_at && activity.updated_at !== activity.created_at;
          const elevated = Boolean(wasEdited || hasRevisited || (myActivityCount >= 2) || total >= 1);
          return (
            <div className={elevated ? 'pt-1' : 'pt-2'} data-testid="continuation-block-host" data-elevated={elevated ? '1' : '0'}>
              <button
                type="button"
                onClick={handleDoThisAgain}
                disabled={duplicateBusy}
                className="text-sm font-medium hover:underline disabled:opacity-60 mr-4"
                style={{ color: elevated ? 'var(--lk-sage)' : 'var(--fy-text-secondary)' }}
                data-testid="do-this-again-btn"
              >
                Do this again
              </button>
              <button
                type="button"
                onClick={handleKeepGoing}
                disabled={keepGoingBusy}
                className="text-sm font-medium hover:underline disabled:opacity-60"
                style={{ color: elevated ? 'var(--lk-sage)' : 'var(--fy-text-secondary)' }}
                data-testid="keep-this-going-btn"
              >
                Keep this going
              </button>
            </div>
          );
        })()}
        {!isHost && activity.source_type === 'group' && activity.source_id && (
          <button
            type="button"
            onClick={() => navigate(`/group/${activity.source_id}`)}
            className="text-sm font-medium hover:underline self-start"
            style={{ color: 'var(--fy-text-secondary)' }}
            data-testid="this-could-continue-btn"
          >
            This could continue
          </button>
        )}
        {showPostJoinHelp && activity.source_id && (
          <button
            type="button"
            onClick={() => navigate(`/group/${activity.source_id}`)}
            className="text-sm hover:underline self-start"
            style={{ color: 'var(--fy-text-disabled)' }}
            data-testid="post-join-help-line"
          >
            You can help keep this going.
          </button>
        )}

        {/* More from this host — upcoming activities by same host. */}
        <MoreFromHost
          activityId={activity.activity_id}
          hostName={activity.host_name || activity.created_by_name}
        />
      </main>
      {/* STICKY JOIN BUTTON — always visible at bottom, above any platform overlay */}
      {status !== 'completed' && !isHost && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[9000] px-4 py-3"
          style={{ backgroundColor: 'var(--fy-background-primary)', borderTop: '1px solid var(--fy-border-subtle)', boxShadow: '0 -4px 12px rgba(0,0,0,0.04)', paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}
          data-testid="sticky-join"
        >
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <div className="flex-1 min-w-0">
              {(() => {
                const total = participantSummary?.total_participants || 0;
                const now = new Date();
                const startsIn = startTime ? Math.round((new Date(startTime) - now) / 60000) : null;
                return (
                  <>
                    <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                      {total >= 3 ? 'Good to go' : total > 0 ? `Needs ${3 - total} more` : 'Needs people'}
                    </p>
                    {startsIn > 0 && startsIn < 480 && (
                      <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                        Starts in {startsIn < 60 ? `${startsIn} min` : `${Math.round(startsIn / 60)} hr`}
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
            <Button
              onClick={() => {
                if (!user) {
                  toast('Create a free account to join', { action: { label: 'Sign up', onClick: () => navigate('/auth?mode=signup') } });
                  return;
                }
                if (mySignal === 'in') return;
                handleSignal('in');
              }}
              className="h-11 px-8 rounded-full text-sm font-bold"
              style={{
                backgroundColor: mySignal === 'in' ? 'var(--fy-surface-muted)' : 'var(--lk-sage)',
                color: mySignal === 'in' ? 'var(--fy-text-primary)' : '#fff',
                border: mySignal === 'in' ? '1px solid var(--fy-border-subtle)' : 'none',
              }}
              data-testid="sticky-join-btn"
            >
              {mySignal === 'in' ? 'Going' : 'Join'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function SharePanel({ activityId, title, imageUrl }) {
  const [showFlyer, setShowFlyer] = useState(false);
  const [flyerTemplate, setFlyerTemplate] = useState('bold');
  const [flyerFormat, setFlyerFormat] = useState('portrait');
  const [generating, setGenerating] = useState(false);
  const [showCircleShare, setShowCircleShare] = useState(false);
  const [myCircles, setMyCircles] = useState([]);
  const [selectedCircles, setSelectedCircles] = useState([]);
  const [sharingToCircles, setSharingToCircles] = useState(false);

  const activityUrl = `${window.location.origin}/activities/${activityId}`;

  const openCircleShare = async () => {
    setShowCircleShare(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/circles`, { headers: { Authorization: `Bearer ${token}` } });
      setMyCircles(res.data?.circles || []);
    } catch {
      setMyCircles([]);
    }
  };

  const toggleCircle = (id) => {
    setSelectedCircles(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const confirmCircleShare = async (all = false) => {
    setSharingToCircles(true);
    try {
      const token = localStorage.getItem('token');
      const body = all ? { all: true } : { circle_ids: selectedCircles };
      await axios.post(
        `${API}/api/activities/${activityId}/share-to-circles`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(all ? 'Shared to all your circles' : 'Shared to your circles');
      setShowCircleShare(false);
      setSelectedCircles([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to share');
    } finally { setSharingToCircles(false); }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(activityUrl);
    trackEvent('link_opened', { activity_id: activityId, method: 'copy_link' });
    toast.success('Link copied');
  };

  const handleShare = async () => {
    try {
      const res = await axios.get(`${API}/api/activities/${activityId}/share-caption`);
      const caption = res.data.caption;
      trackEvent('link_opened', { activity_id: activityId, method: 'share' });

      if (navigator.share) {
        await navigator.share({ title: title || 'Activity', text: caption, url: activityUrl });
      } else {
        navigator.clipboard.writeText(`${caption}\n${activityUrl}`);
        toast.success('Caption + link copied');
      }
    } catch { /* user canceled */ }
  };

  const handleDownloadFlyer = async () => {
    setGenerating(true);
    try {
      const res = await axios.get(`${API}/api/activities/${activityId}/flyer?template=${flyerTemplate}&format=${flyerFormat}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = flyerFormat === 'print' ? 'flyer.pdf' : `flyer_${flyerFormat}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to generate flyer');
    } finally {
      setGenerating(false);
    }
  };

  const handleShareFlyer = async () => {
    setGenerating(true);
    try {
      const [flyerRes, captionRes] = await Promise.all([
        axios.get(`${API}/api/activities/${activityId}/flyer?template=${flyerTemplate}&format=square`, { responseType: 'blob' }),
        axios.get(`${API}/api/activities/${activityId}/share-caption`)
      ]);

      if (navigator.share && navigator.canShare) {
        const file = new File([flyerRes.data], 'flyer.png', { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: captionRes.data.caption });
        } else {
          // Fallback: share text only
          await navigator.share({ text: captionRes.data.caption, url: activityUrl });
        }
      } else {
        // Fallback: download
        const url = URL.createObjectURL(flyerRes.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'flyer.png';
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Flyer downloaded');
      }
    } catch { /* canceled */ }
    finally { setGenerating(false); }
  };

  return (
    <div className="space-y-2" data-testid="share-panel">
      {/* Share with my circles — opens modal */}
      <button
        onClick={openCircleShare}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium"
        style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }}
        data-testid="share-to-circles-btn"
      >
        <Users className="w-4 h-4" />
        Share with my circles
      </button>

      {showCircleShare && (
        <div
          className="fixed inset-0 z-[9500] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
          onClick={() => setShowCircleShare(false)}
          data-testid="circle-share-modal"
        >
          <div
            className="w-full max-w-sm rounded-2xl p-4"
            style={{ backgroundColor: 'var(--fy-surface-card)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-bold mb-1" style={{ color: 'var(--fy-text-primary)' }}>Share with your circles</h3>
            <p className="text-xs mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
              Appears in members' "From Your Circles" stream.
            </p>
            {myCircles.length === 0 ? (
              <p className="text-sm py-6 text-center" style={{ color: 'var(--fy-text-disabled)' }}>
                You don't have any circles yet.
              </p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {myCircles.map(c => {
                  const checked = selectedCircles.includes(c.id);
                  return (
                    <label
                      key={c.id}
                      className="flex items-center gap-3 p-3 rounded-xl cursor-pointer"
                      style={{ backgroundColor: checked ? 'var(--lk-sage-muted, #EDF1E8)' : 'var(--fy-surface-muted)', border: `1px solid ${checked ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}` }}
                      data-testid={`circle-option-${c.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleCircle(c.id)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm flex-1" style={{ color: 'var(--fy-text-primary)' }}>{c.name}</span>
                      <span className="text-[11px]" style={{ color: 'var(--fy-text-disabled)' }}>{c.member_count || 0} members</span>
                    </label>
                  );
                })}
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setShowCircleShare(false)}
                className="flex-1 py-2.5 rounded-full text-sm font-medium"
                style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-primary)' }}
                data-testid="circle-share-cancel"
              >
                Cancel
              </button>
              {myCircles.length > 0 && (
                <>
                  <button
                    onClick={() => confirmCircleShare(true)}
                    disabled={sharingToCircles}
                    className="flex-1 py-2.5 rounded-full text-sm font-medium"
                    style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--lk-sage)', color: 'var(--lk-sage)' }}
                    data-testid="circle-share-all"
                  >
                    All circles
                  </button>
                  <button
                    onClick={() => confirmCircleShare(false)}
                    disabled={sharingToCircles || selectedCircles.length === 0}
                    className="flex-1 py-2.5 rounded-full text-sm font-medium"
                    style={{ backgroundColor: 'var(--lk-sage)', color: '#fff', opacity: (selectedCircles.length === 0 || sharingToCircles) ? 0.6 : 1 }}
                    data-testid="circle-share-confirm"
                  >
                    {sharingToCircles ? 'Sharing...' : 'Share'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick actions row */}
      <div className="flex items-center gap-2">
        <button
          onClick={handleShare}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium"
          style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
          data-testid="share-btn"
        >
          <Share2 className="w-4 h-4" />
          Share
        </button>
        <button
          onClick={handleCopyLink}
          className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium"
          style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
          data-testid="copy-link-btn"
        >
          <Copy className="w-4 h-4" />
          Copy Link
        </button>
        <button
          onClick={() => setShowFlyer(!showFlyer)}
          className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium"
          style={{
            backgroundColor: showFlyer ? 'var(--lk-sage-muted, #EDF1E8)' : 'var(--fy-surface-card)',
            border: `1px solid ${showFlyer ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`,
            color: showFlyer ? 'var(--lk-sage)' : 'var(--fy-text-primary)'
          }}
          data-testid="create-flyer-btn"
        >
          <Image className="w-4 h-4" />
          Flyer
        </button>
      </div>

      {/* Flyer generator panel */}
      {showFlyer && (
        <Card className="p-4 space-y-4" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }} data-testid="flyer-panel">
          {/* Template selection */}
          <div>
            <p className="text-xs font-medium mb-2" style={{ color: 'var(--fy-text-secondary)' }}>Template</p>
            <div className="flex gap-2">
              {[
                { id: 'bold', label: 'Bold' },
                { id: 'clean', label: 'Clean' },
                { id: 'minimal', label: 'Minimal' },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setFlyerTemplate(t.id)}
                  className="flex-1 py-1.5 rounded-lg text-xs font-medium"
                  style={{
                    backgroundColor: flyerTemplate === t.id ? 'var(--lk-sage)' : 'var(--fy-surface-muted)',
                    color: flyerTemplate === t.id ? '#fff' : 'var(--fy-text-primary)',
                  }}
                  data-testid={`flyer-template-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Format selection */}
          <div>
            <p className="text-xs font-medium mb-2" style={{ color: 'var(--fy-text-secondary)' }}>Format</p>
            <div className="flex gap-2">
              {[
                { id: 'portrait', label: 'Portrait (PNG)' },
                { id: 'letter', label: 'US Letter (PDF)' },
                { id: 'a4', label: 'A4 (PDF)' },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setFlyerFormat(f.id)}
                  className="flex-1 py-1.5 rounded-lg text-xs font-medium"
                  style={{
                    backgroundColor: flyerFormat === f.id ? 'var(--lk-sage)' : 'var(--fy-surface-muted)',
                    color: flyerFormat === f.id ? '#fff' : 'var(--fy-text-primary)',
                  }}
                  data-testid={`flyer-format-${f.id}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Flyer preview — shows the exact image used on the flyer (same as Card & Detail) */}
          <div className="rounded-lg overflow-hidden" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
            <div className="w-full aspect-[16/9] overflow-hidden" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                  data-testid="flyer-image-preview"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
                  No cover image
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 p-3">
              <img
                src={`${API}/api/activities/${activityId}/qr`}
                alt="QR Code"
                className="w-14 h-14 rounded flex-shrink-0"
                data-testid="activity-qr"
              />
              <div className="min-w-0">
                <p className="text-xs font-medium truncate" style={{ color: 'var(--fy-text-primary)' }}>{title}</p>
                <p className="text-[10px]" style={{ color: 'var(--fy-text-disabled)' }}>Scan to see details</p>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            <Button
              onClick={handleDownloadFlyer}
              disabled={generating}
              className="flex-1 h-10 rounded-full text-sm font-medium"
              style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-primary)' }}
              data-testid="download-flyer-btn"
            >
              <Download className="w-4 h-4 mr-1" />
              {generating ? 'Generating...' : 'Download'}
            </Button>
            <Button
              onClick={handleShareFlyer}
              disabled={generating}
              className="flex-1 h-10 rounded-full text-sm font-semibold"
              style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }}
              data-testid="share-flyer-btn"
            >
              <Share2 className="w-4 h-4 mr-1" />
              Share Flyer
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
